import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import {
    CloseOutlined,
    LineChartOutlined,
    ReloadOutlined,
    UnorderedListOutlined,
} from '@ant-design/icons'
import {
    Alert,
    Button,
    Empty,
    Image,
    Modal,
    Pagination,
    Select,
    Spin,
    Table,
    Tag,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'

import { FilterActions, FilterBar, FilterField } from '../common/FilterBar'
import { buildReadonlyContainedPreviewRender } from '../common/MediaPreview/readonlyPreviewRender'
import { buildPaginatedListPagination } from '../common/PaginatedListTable'
import { ReadonlyRangePicker } from '../common/DatePickerControls'
import {
    buildAllFilterOptions,
    getAllAwareDisplayValues,
    normalizeAllAwareValues,
    type FilterOption,
    type FilterSelectValue,
} from '../statistics/filterSelectUtils'
import {
    DeviceDetectionRecord,
    PaginatedDetectionResponse,
} from '../../types/device-mosquito'
import {
    STATISTICS_REQUEST_TIMEOUT_MS,
    getStatisticsData,
} from '../../services/statisticsService'
import { getDeviceDetections } from '../../services/deviceMosquitoService'
import { usePestTypeOptions } from '../../services/pestTypeOptionsService'
import { localeService } from '../../services/localeService'
import { getFriendlyErrorMessage } from '../../services/errorHandler'
import { getImageUrl } from '../../utils/urlHelper'
import { filterTrendDataByMaxDate } from '../../utils/trendDateUtils'
import {
    BASIC_DEVICE_BOX_COLOR,
    shouldUseBasicBoxOnlyHistoryImage,
} from '../../utils/historyCapabilities'
import {
    isDevicePackageKey,
    type DevicePackageKey,
} from '../../constants/devicePackages'
import { ReadonlyDetectionOverlayImage } from '../common/ReadonlyDetectionOverlayImage'
import {
    HISTORY_DETECTION_CLASS_DISPLAY_PREFIX,
    buildConfiguredPestTypeFilterOptions,
    getMosquitoCategoryDisplayName,
    getPredictionDisplayCategory,
    normalizeConfiguredDisplayCategory,
    sortMosquitoDisplayCategories,
    type MosquitoDisplayCategory,
} from '../../utils/mosquitoDisplay'
import { filterSearchOptionByLabel } from '../../utils/searchText'
import './DeviceHistoryModal.css'

const DEFAULT_PAGE_SIZE = 5
const DEVICE_HISTORY_PAGE_SIZE_OPTIONS = ['5', '10', '20', '50']
const HISTORY_MODAL_BODY_STYLE: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 'min(720px, calc(100vh - 96px))',
    overflowX: 'hidden',
    overflowY: 'auto',
}
const HISTORY_MODAL_STYLE: React.CSSProperties = {
    top: 16,
}
const INITIAL_THUMBNAIL_LOAD_LIMIT = 6
const HISTORY_TREND_CHART_WIDTH = 920
const HISTORY_TREND_CHART_HEIGHT = 260
const HISTORY_TREND_CHART_PADDING_TOP = 24
const HISTORY_TREND_CHART_PADDING_RIGHT = 24
const HISTORY_TREND_CHART_PADDING_BOTTOM = 42
const HISTORY_TREND_CHART_PADDING_LEFT = 48
const HISTORY_TREND_Y_TICK_COUNT = 5
const HISTORY_TREND_X_TICK_COUNT = 8
const HISTORY_THUMBNAIL_PLACEHOLDER_STYLE: React.CSSProperties = {
    width: 80,
    height: 60,
    borderRadius: 4,
    backgroundColor: '#f0f2f5',
}
const HISTORY_THUMBNAIL_STATUS_STYLE: React.CSSProperties = {
    ...HISTORY_THUMBNAIL_PLACEHOLDER_STYLE,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#8c8c8c',
    fontSize: 12,
    lineHeight: 1.4,
    textAlign: 'center',
}
const FEATURE_HISTORY_TRENDS = 'history_trends'
const FEATURE_MOSQUITO_SPECIES_RECOGNITION = 'mosquito_species_recognition'

const requiresSpeciesRecognitionFilter = (pestTypes: string[]) =>
    pestTypes.some(
        (pestType) =>
            normalizeConfiguredDisplayCategory(pestType) !== 'otherMosquito'
    )

const BUILTIN_PEST_TYPE_ALIASES: Record<string, string[]> = {
    culex: ['culex', '库蚊'],
    aedes: ['aedes', '伊蚊'],
    otherMosquito: ['otherMosquito', '其他蚊虫'],
}

const sanitizePestTypesForSpeciesSupport = (
    pestTypes: string[],
    supportsSpeciesRecognition: boolean
) => {
    if (supportsSpeciesRecognition) {
        return pestTypes
    }

    return []
}

const normalizePestTypeMatchValue = (value: unknown) =>
    typeof value === 'string' ? value.trim() : ''

const addPestTypeMatchValue = (values: Set<string>, value: unknown) => {
    const normalized = normalizePestTypeMatchValue(value)
    if (normalized) {
        values.add(normalized)
    }
}

const buildPestTypeMatchValues = (
    pestType: string,
    pestTypeOptions: Array<FilterOption<FilterSelectValue<string>>>
) => {
    const values = new Set<string>()
    addPestTypeMatchValue(values, pestType)

    const option = pestTypeOptions.find((item) => item.value === pestType)
    if (option) {
        addPestTypeMatchValue(values, option.label)
    }

    BUILTIN_PEST_TYPE_ALIASES[pestType]?.forEach((value) =>
        addPestTypeMatchValue(values, value)
    )
    Object.values(BUILTIN_PEST_TYPE_ALIASES).forEach((aliases) => {
        if (aliases.includes(pestType)) {
            aliases.forEach((value) => addPestTypeMatchValue(values, value))
        }
    })

    return values
}

const getPredictionPestTypeMatchValues = (
    result: Record<string, unknown>
) => {
    const values = new Set<string>()
    addPestTypeMatchValue(values, result.display_category_key)
    addPestTypeMatchValue(values, result.display_category_name)
    addPestTypeMatchValue(values, getPredictionDisplayCategory(result))
    return values
}

const doesPredictionMatchPestType = (
    result: Record<string, unknown>,
    pestTypeMatchValues: Set<string>
) => {
    const predictionValues = getPredictionPestTypeMatchValues(result)
    for (const value of predictionValues) {
        if (pestTypeMatchValues.has(value)) {
            return true
        }
    }
    return false
}

const isCanceledRequestError = (error: unknown): boolean => {
    if (!error || typeof error !== 'object') {
        return false
    }
    const maybeError = error as { code?: string; name?: string }
    return (
        maybeError.code === 'ERR_CANCELED' ||
        maybeError.name === 'AbortError' ||
        maybeError.name === 'CanceledError'
    )
}

interface DeviceHistoryModalProps {
    visible: boolean
    deviceId: string | null
    deviceAddress?: string
    deviceSnapshot?: DeviceHistorySnapshot | null
    onClose: () => void
}

type HistoryModalView = 'list' | 'trend'

interface DeviceHistorySnapshot {
    id?: string | null
    type?: number | null
    package_key?: string | null
    purchase_version?: string | null
    supports_species_recognition?: boolean | null
    name?: string | null
    address?: string | null
    device_code?: string | null
    project_id?: number | null
}

interface HistoryTrendPoint {
    date: string
    count: number
    x: number
    y: number
}

type TrendTooltipPlacement = 'left' | 'right'

const HISTORY_TREND_TOOLTIP_OFFSET = 12
const HISTORY_TREND_TOOLTIP_ESTIMATED_WIDTH = 136
const HISTORY_TREND_TOOLTIP_EDGE_GAP = 12

const getHistoryTrendCount = (
    record: DeviceDetectionRecord,
    selectedPestTypes: string[],
    supportsSpeciesRecognition: boolean,
    pestTypeOptions: Array<FilterOption<FilterSelectValue<string>>> = []
) => {
    const effectivePestTypes = sanitizePestTypesForSpeciesSupport(
        selectedPestTypes,
        supportsSpeciesRecognition
    )

    if (selectedPestTypes.length > 0 && effectivePestTypes.length === 0) {
        return 0
    }

    if (effectivePestTypes.length === 0) {
        return record.mosquito_count
    }

    if (!record.feature_keys_at_upload?.includes(FEATURE_HISTORY_TRENDS)) {
        return 0
    }

    if (!supportsSpeciesRecognition) {
        return 0
    }

    if (
        requiresSpeciesRecognitionFilter(effectivePestTypes) &&
        !record.feature_keys_at_upload?.includes(
            FEATURE_MOSQUITO_SPECIES_RECOGNITION
        )
    ) {
        return 0
    }

    if (!record.results || record.results.length === 0) {
        return 0
    }

    return record.results.filter((result) =>
        effectivePestTypes.some((pestType) =>
            doesPredictionMatchPestType(
                result,
                buildPestTypeMatchValues(pestType, pestTypeOptions)
            )
        )
    ).length
}

const getHistoryTrendMaxCount = (
    series: Array<{ date: string; count: number }>
) => {
    const rawMax = Math.max(0, ...series.map((item) => item.count))
    return Math.max(4, Math.ceil(rawMax / 4) * 4)
}

const buildHistoryTrendPoints = (
    series: Array<{ date: string; count: number }>
): HistoryTrendPoint[] => {
    if (series.length === 0) {
        return []
    }

    const maxCount = getHistoryTrendMaxCount(series)
    const innerWidth =
        HISTORY_TREND_CHART_WIDTH -
        HISTORY_TREND_CHART_PADDING_LEFT -
        HISTORY_TREND_CHART_PADDING_RIGHT
    const innerHeight =
        HISTORY_TREND_CHART_HEIGHT -
        HISTORY_TREND_CHART_PADDING_TOP -
        HISTORY_TREND_CHART_PADDING_BOTTOM

    return series.map((item, index) => {
        const x =
            series.length === 1
                ? HISTORY_TREND_CHART_WIDTH / 2
                : HISTORY_TREND_CHART_PADDING_LEFT +
                  (innerWidth * index) / Math.max(1, series.length - 1)
        const y =
            HISTORY_TREND_CHART_PADDING_TOP +
            innerHeight -
            (Math.max(0, item.count) / maxCount) * innerHeight

        return {
            ...item,
            x,
            y,
        }
    })
}

const getTrendTooltipPlacement = (
    pointX: number
): TrendTooltipPlacement => {
    const availableRight = HISTORY_TREND_CHART_WIDTH - pointX
    const availableLeft = pointX
    const requiredSpace =
        HISTORY_TREND_TOOLTIP_ESTIMATED_WIDTH + HISTORY_TREND_TOOLTIP_OFFSET
    const canFitRight =
        availableRight >= requiredSpace + HISTORY_TREND_TOOLTIP_EDGE_GAP
    const canFitLeft =
        availableLeft >= requiredSpace + HISTORY_TREND_TOOLTIP_EDGE_GAP

    if (canFitRight && !canFitLeft) {
        return 'right'
    }

    if (canFitLeft && !canFitRight) {
        return 'left'
    }

    if (canFitRight && canFitLeft) {
        return 'right'
    }

    return availableRight >= availableLeft ? 'right' : 'left'
}

const formatTrendAxisDate = (date: string) => {
    const parsedDate = dayjs(date)
    return parsedDate.isValid() ? parsedDate.format('MM/DD') : date
}

const normalizeTrendPestTypes = (pestTypes: string[]) =>
    [...new Set(pestTypes)].sort()

const PACKAGE_KEY_BY_VERSION_NAME: Record<string, DevicePackageKey> = {
    '进阶侦测版': 'foundation',
    '专业甄别版': 'recognition',
    '全能大师版': 'insight',
}

const resolveDevicePackageKey = (
    snapshot?: DeviceHistorySnapshot | null
): DevicePackageKey | null => {
    if (isDevicePackageKey(snapshot?.package_key)) {
        return snapshot.package_key
    }

    const purchaseVersion =
        typeof snapshot?.purchase_version === 'string'
            ? snapshot.purchase_version.trim()
            : ''
    if (purchaseVersion && PACKAGE_KEY_BY_VERSION_NAME[purchaseVersion]) {
        return PACKAGE_KEY_BY_VERSION_NAME[purchaseVersion]
    }

    if (snapshot?.supports_species_recognition === false) {
        return 'foundation'
    }

    if (snapshot?.supports_species_recognition === true) {
        return 'insight'
    }

    return null
}

const resolveTrendAnalysisFeature = (
    snapshot?: DeviceHistorySnapshot | null
): 'mosquito_count_monitoring' | 'trend_analysis' => {
    const packageKey = resolveDevicePackageKey(snapshot)

    if (packageKey === 'insight') {
        return 'trend_analysis'
    }

    return 'mosquito_count_monitoring'
}

const buildTrendQueryKey = (
    deviceId: string,
    dateRange: [Dayjs, Dayjs] | null,
    pestTypes: string[],
    analysisFeature: string
) =>
    JSON.stringify({
        deviceId,
        startDate: dateRange?.[0]?.format('YYYY-MM-DD') ?? null,
        endDate: dateRange?.[1]?.format('YYYY-MM-DD') ?? null,
        pestTypes: normalizeTrendPestTypes(pestTypes),
        analysisFeature,
    })

const getDeviceModelDisplayName = (
    type: number | null | undefined,
    t: ReturnType<typeof useTranslation>['t']
) => {
    if (type === 1) {
        return t('management.deviceTypes.mosquitoDevice', {
            defaultValue: '灭蚊设备',
        })
    }

    if (type == null) {
        return '-'
    }

    return t('management.deviceTypes.other', {
        defaultValue: '其他',
    })
}

const DeviceHistoryModal: React.FC<DeviceHistoryModalProps> = ({
    visible,
    deviceId,
    deviceAddress,
    deviceSnapshot,
    onClose,
}) => {
    const { t } = useTranslation(['device', 'history'])
    const [loading, setLoading] = useState(false)
    const [trendLoading, setTrendLoading] = useState(false)
    const [data, setData] = useState<PaginatedDetectionResponse | null>(null)
    const [trendData, setTrendData] = useState<Array<{ date: string; count: number }>>(
        []
    )
    const [currentPage, setCurrentPage] = useState(1)
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
    const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null)
    const [selectedPestTypes, setSelectedPestTypes] = useState<string[]>([])
    const [activeView, setActiveView] = useState<HistoryModalView>('list')
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [trendErrorMessage, setTrendErrorMessage] = useState<string | null>(null)
    const [hoveredTrendPoint, setHoveredTrendPoint] =
        useState<HistoryTrendPoint | null>(null)
    const [enabledThumbnailCount, setEnabledThumbnailCount] = useState(
        INITIAL_THUMBNAIL_LOAD_LIMIT
    )
    const [failedMediaKeys, setFailedMediaKeys] = useState<Set<string>>(
        () => new Set()
    )
    const requestIdRef = useRef(0)
    const abortControllerRef = useRef<AbortController | null>(null)
    const trendRequestIdRef = useRef(0)
    const trendAbortControllerRef = useRef<AbortController | null>(null)
    const trendInFlightKeyRef = useRef<string | null>(null)
    const trendLoadedKeyRef = useRef<string | null>(null)
    const initialLoading =
        visible && Boolean(deviceId) && data === null && !errorMessage
    const historyLoading = loading || initialLoading
    const supportsSpeciesRecognition =
        deviceSnapshot?.supports_species_recognition !== false
    const trendAnalysisFeature = resolveTrendAnalysisFeature(deviceSnapshot)
    const { options: configuredPestTypeOptions } = usePestTypeOptions(
        deviceSnapshot?.project_id == null ? [] : [deviceSnapshot.project_id],
        deviceSnapshot?.project_id != null
    )

    const fetchData = useCallback(
        async (
            page: number,
            nextPageSize: number,
            nextDateRange: [Dayjs, Dayjs] | null,
            nextPestTypes: string[],
            forceRefresh = false
        ) => {
            if (!deviceId) {
                return
            }

            abortControllerRef.current?.abort()
            const requestId = requestIdRef.current + 1
            requestIdRef.current = requestId
            const abortController = new AbortController()
            abortControllerRef.current = abortController

            setLoading(true)
            setErrorMessage(null)
            setEnabledThumbnailCount(INITIAL_THUMBNAIL_LOAD_LIMIT)
            setFailedMediaKeys(new Set())
            try {
                const effectivePestTypes = sanitizePestTypesForSpeciesSupport(
                    nextPestTypes,
                    supportsSpeciesRecognition
                )
                const filters = {
                    startTime: nextDateRange?.[0]?.startOf('day').valueOf(),
                    endTime: nextDateRange?.[1]?.endOf('day').valueOf(),
                    pestTypes: effectivePestTypes,
                    countMode: 'history_modal' as const,
                }
                const response = forceRefresh
                    ? await getDeviceDetections(
                          deviceId,
                          page,
                          nextPageSize,
                          filters,
                          true,
                          { signal: abortController.signal }
                      )
                    : await getDeviceDetections(
                          deviceId,
                          page,
                          nextPageSize,
                          filters,
                          false,
                          { signal: abortController.signal }
                      )
                if (requestIdRef.current === requestId) {
                    setData(response)
                }
            } catch (error) {
                if (
                    isCanceledRequestError(error) ||
                    requestIdRef.current !== requestId
                ) {
                    return
                }
                console.error('获取设备历史记录失败:', error)
                setErrorMessage(
                    getFriendlyErrorMessage(error) || t('history.loadFailed')
                )
            } finally {
                if (requestIdRef.current === requestId) {
                    setLoading(false)
                    if (abortControllerRef.current === abortController) {
                        abortControllerRef.current = null
                    }
                }
            }
        },
        [deviceId, supportsSpeciesRecognition, t]
    )

    const fetchTrendData = useCallback(
        async (
            nextDateRange: [Dayjs, Dayjs] | null,
            nextPestTypes: string[],
            forceRefresh = false
        ) => {
            if (!deviceId) {
                return
            }

            const normalizedPestTypes = normalizeTrendPestTypes(nextPestTypes)
            const queryKey = buildTrendQueryKey(
                deviceId,
                nextDateRange,
                normalizedPestTypes,
                trendAnalysisFeature
            )

            if (trendInFlightKeyRef.current === queryKey) {
                return
            }

            if (!forceRefresh && trendLoadedKeyRef.current === queryKey) {
                return
            }

            if (
                trendInFlightKeyRef.current &&
                trendInFlightKeyRef.current !== queryKey
            ) {
                trendAbortControllerRef.current?.abort()
                trendAbortControllerRef.current = null
                trendInFlightKeyRef.current = null
            }

            if (forceRefresh && trendLoadedKeyRef.current === queryKey) {
                trendLoadedKeyRef.current = null
            }

            const requestId = trendRequestIdRef.current + 1
            trendRequestIdRef.current = requestId
            const abortController = new AbortController()
            trendAbortControllerRef.current = abortController
            trendInFlightKeyRef.current = queryKey
            setTrendLoading(true)
            setTrendErrorMessage(null)
            try {
                const response = await getStatisticsData(
                    nextDateRange?.[0]?.format('YYYY-MM-DD'),
                    nextDateRange?.[1]?.format('YYYY-MM-DD'),
                    {
                        projectIds: [],
                        regionIds: [],
                        deviceIds: deviceId ? [deviceId] : [],
                        pestTypes: normalizedPestTypes,
                    },
                    trendAnalysisFeature,
                    {
                        signal: abortController.signal,
                        timeout: STATISTICS_REQUEST_TIMEOUT_MS,
                    }
                )

                if (requestId === trendRequestIdRef.current) {
                    setTrendData(
                        filterTrendDataByMaxDate(
                            response.summary.trendData ?? [],
                            response.businessDate
                        )
                    )
                    trendLoadedKeyRef.current = queryKey
                }
            } catch (error) {
                if (
                    isCanceledRequestError(error) ||
                    requestId !== trendRequestIdRef.current
                ) {
                    return
                }
                console.error('获取设备趋势数据失败:', error)
                if (requestId === trendRequestIdRef.current) {
                    setTrendData([])
                    setTrendErrorMessage(
                        getFriendlyErrorMessage(error) || t('history.loadFailed')
                    )
                }
            } finally {
                if (requestId === trendRequestIdRef.current) {
                    setTrendLoading(false)
                    if (trendAbortControllerRef.current === abortController) {
                        trendAbortControllerRef.current = null
                    }
                    if (trendInFlightKeyRef.current === queryKey) {
                        trendInFlightKeyRef.current = null
                    }
                }
            }
        },
        [deviceId, t, trendAnalysisFeature]
    )

    useEffect(() => {
        if (visible) {
            return
        }
        requestIdRef.current += 1
        abortControllerRef.current?.abort()
        abortControllerRef.current = null
        trendRequestIdRef.current += 1
        trendAbortControllerRef.current?.abort()
        trendAbortControllerRef.current = null
        trendInFlightKeyRef.current = null
        trendLoadedKeyRef.current = null
        setLoading(false)
        setTrendLoading(false)
        setData(null)
        setTrendData([])
        setErrorMessage(null)
        setTrendErrorMessage(null)
        setHoveredTrendPoint(null)
    }, [visible])

    useEffect(() => {
        if (!visible || !deviceId) {
            return
        }

        trendRequestIdRef.current += 1
        trendAbortControllerRef.current?.abort()
        trendAbortControllerRef.current = null
        trendInFlightKeyRef.current = null
        trendLoadedKeyRef.current = null

        setCurrentPage(1)
        setPageSize(DEFAULT_PAGE_SIZE)
        setDateRange(null)
        setSelectedPestTypes([])
        setActiveView('list')
        setData(null)
        setTrendData([])
        setTrendLoading(false)
        setHoveredTrendPoint(null)
        setErrorMessage(null)
        setTrendErrorMessage(null)
        void fetchData(1, DEFAULT_PAGE_SIZE, null, [], true)
        void fetchTrendData(null, [])
    }, [deviceId, fetchData, fetchTrendData, visible])

    useEffect(() => {
        if (!visible || !deviceId || activeView !== 'trend') {
            return
        }

        void fetchTrendData(dateRange, selectedPestTypes)
    }, [
        activeView,
        dateRange,
        deviceId,
        fetchTrendData,
        selectedPestTypes,
        visible,
    ])

    const pestTypeSelectOptions = useMemo(
        () => {
            const configuredOptions = buildConfiguredPestTypeFilterOptions(
                configuredPestTypeOptions.length > 0
                    ? configuredPestTypeOptions
                    : data?.pest_type_options ?? []
            )

            return buildAllFilterOptions(
                t('history.filters.allPestType'),
                supportsSpeciesRecognition ? configuredOptions : []
            )
        },
        [
            configuredPestTypeOptions,
            data?.pest_type_options,
            supportsSpeciesRecognition,
            t,
        ]
    )

    const handleResetFilters = () => {
        setDateRange(null)
        setSelectedPestTypes([])
        setCurrentPage(1)
        void fetchData(1, pageSize, null, [])
    }

    const handleRefresh = () => {
        void fetchData(currentPage, pageSize, dateRange, selectedPestTypes, true)
        if (activeView === 'trend') {
            void fetchTrendData(dateRange, selectedPestTypes, true)
        }
    }

    useEffect(() => {
        if (!visible || !data || enabledThumbnailCount >= data.items.length * 2) {
            return
        }

        const timer = window.setTimeout(() => {
            setEnabledThumbnailCount((currentCount) =>
                Math.min(
                    currentCount + INITIAL_THUMBNAIL_LOAD_LIMIT,
                    data.items.length * 2
                )
            )
        }, 120)

        return () => {
            window.clearTimeout(timer)
        }
    }, [data, enabledThumbnailCount, visible])

    const historyPagination = useMemo(
        () =>
            data
                ? {
                      ...buildPaginatedListPagination({
                          current: currentPage,
                          pageSize,
                          total: data.total,
                          showTotal: (total) =>
                              t('history.totalRecords', { total }),
                          onChange: (page, nextPageSize) => {
                              const resolvedPageSize =
                                  nextPageSize ?? pageSize
                              const resolvedPage =
                                  resolvedPageSize !== pageSize ? 1 : page
                              setPageSize(resolvedPageSize)
                              setCurrentPage(resolvedPage)
                              void fetchData(
                                  resolvedPage,
                                  resolvedPageSize,
                                  dateRange,
                                  selectedPestTypes
                              )
                          },
                      }),
                      pageSizeOptions: DEVICE_HISTORY_PAGE_SIZE_OPTIONS,
                  }
                : null,
        [
            currentPage,
            data,
            dateRange,
            fetchData,
            pageSize,
            selectedPestTypes,
            t,
        ]
    )

    const formatDate = (dateString: string) =>
        localeService.formatDate(dateString, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        })

    const trendSeries = trendData
    const trendPoints = useMemo(
        () => buildHistoryTrendPoints(trendSeries),
        [trendSeries]
    )
    const trendMaxCount = useMemo(
        () => getHistoryTrendMaxCount(trendSeries),
        [trendSeries]
    )
    const trendYAxisTicks = useMemo(() => {
        const innerHeight =
            HISTORY_TREND_CHART_HEIGHT -
            HISTORY_TREND_CHART_PADDING_TOP -
            HISTORY_TREND_CHART_PADDING_BOTTOM

        return Array.from({ length: HISTORY_TREND_Y_TICK_COUNT }, (_, index) => {
            const ratio = index / Math.max(1, HISTORY_TREND_Y_TICK_COUNT - 1)
            return {
                value: Math.round(trendMaxCount * (1 - ratio)),
                y: HISTORY_TREND_CHART_PADDING_TOP + innerHeight * ratio,
            }
        })
    }, [trendMaxCount])
    const trendXAxisTicks = useMemo(() => {
        if (trendPoints.length <= HISTORY_TREND_X_TICK_COUNT) {
            return trendPoints
        }

        const step = Math.ceil(
            (trendPoints.length - 1) /
                Math.max(1, HISTORY_TREND_X_TICK_COUNT - 1)
        )

        return trendPoints.filter(
            (_, index) =>
                index === 0 ||
                index === trendPoints.length - 1 ||
                index % step === 0
        )
    }, [trendPoints])
    const trendLinePath = trendPoints
        .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
        .join(' ')
    const trendTotalCount = trendSeries.reduce((sum, item) => sum + item.count, 0)
    const trendAverageCount =
        trendSeries.length > 0 ? trendTotalCount / trendSeries.length : 0
    const trendPeak = trendSeries.reduce<{ date: string; count: number } | null>(
        (currentPeak, item) =>
            !currentPeak || item.count > currentPeak.count ? item : currentPeak,
        null
    )
    const deviceTitleLabel =
        deviceSnapshot?.name ||
        deviceAddress ||
        deviceSnapshot?.address ||
        deviceSnapshot?.device_code
    const deviceSummaryName = deviceTitleLabel || '-'
    const deviceSummaryVersion =
        deviceSnapshot?.purchase_version ||
        t('viewModal.notSet', {
            defaultValue: '未设置',
        })
    const deviceSummaryModel = getDeviceModelDisplayName(deviceSnapshot?.type, t)
    const deviceSummaryId = deviceSnapshot?.id || deviceId || '-'
    const deviceSummaryItems = [
        {
            label: t('history.summary.deviceName', {
                defaultValue: '设备名称',
            }),
            value: deviceSummaryName,
        },
        {
            label: t('history.summary.deviceVersion', {
                defaultValue: '设备版本',
            }),
            value: deviceSummaryVersion,
            isTag: Boolean(deviceSnapshot?.purchase_version),
        },
        {
            label: t('history.summary.deviceModel', {
                defaultValue: '设备型号',
            }),
            value: deviceSummaryModel,
        },
        {
            label: t('history.summary.deviceId', {
                defaultValue: '设备ID',
            }),
            value: deviceSummaryId,
        },
    ]
    const handleTrendMouseMove = useCallback(
        (event: React.MouseEvent<SVGSVGElement>) => {
            if (trendPoints.length === 0) {
                setHoveredTrendPoint(null)
                return
            }

            const svgRect = event.currentTarget.getBoundingClientRect()
            const pointerX =
                ((event.clientX - svgRect.left) / Math.max(1, svgRect.width)) *
                HISTORY_TREND_CHART_WIDTH
            const nearestPoint = trendPoints.reduce((nearest, point) =>
                Math.abs(point.x - pointerX) < Math.abs(nearest.x - pointerX)
                    ? point
                    : nearest
            )
            setHoveredTrendPoint(nearestPoint)
        },
        [trendPoints]
    )
    const handleTrendMouseLeave = useCallback(() => {
        setHoveredTrendPoint(null)
    }, [])
    const trendTooltipPlacement = hoveredTrendPoint
        ? getTrendTooltipPlacement(hoveredTrendPoint.x)
        : 'right'

    const renderHistoryImage = (
        record: DeviceDetectionRecord,
        path: string | null,
        alt: string,
        emptyText: string,
        thumbnailIndex: number
    ) => {
        if (record.media_expired) {
            return (
                <div style={HISTORY_THUMBNAIL_STATUS_STYLE}>
                    {t('history.mediaExpired')}
                </div>
            )
        }

        if (!path) {
            return <span style={{ color: '#999' }}>{emptyText}</span>
        }

        const imageUrl = getImageUrl(path)
        const shouldLoadThumbnail = thumbnailIndex < enabledThumbnailCount
        const mediaKey = `${record.id}:${alt}:${path}`

        if (failedMediaKeys.has(mediaKey)) {
            return (
                <div style={HISTORY_THUMBNAIL_STATUS_STYLE}>
                    {t('history.imageLoadFailed')}
                </div>
            )
        }

        return (
            <Image
                src={shouldLoadThumbnail ? imageUrl : undefined}
                alt={alt}
                width={80}
                height={60}
                loading="lazy"
                decoding="async"
                wrapperClassName="device-history-thumbnail"
                placeholder={
                    <div style={HISTORY_THUMBNAIL_PLACEHOLDER_STYLE} />
                }
                onError={() => {
                    setFailedMediaKeys((current) => {
                        const next = new Set(current)
                        next.add(mediaKey)
                        return next
                    })
                }}
                style={{ objectFit: 'cover', borderRadius: 4 }}
                preview={{
                    src: imageUrl,
                    mask: t('history.preview'),
                    imageRender: buildReadonlyContainedPreviewRender(),
                }}
            />
        )
    }

    const renderAnnotatedHistoryImage = (
        record: DeviceDetectionRecord,
        thumbnailIndex: number
    ) => {
        const alt = t('history.columns.annotatedImage')
        const emptyText = t('history.noAnnotation')
        const fallbackAnnotatedImagePath =
            record.effective_source === 'manual'
                ? record.effective_annotated_path ||
                  record.annotated_path ||
                  null
                : record.annotated_path ||
                  record.effective_annotated_path ||
                  null
        const overlayImagePath = record.image_path || fallbackAnnotatedImagePath

        if (record.media_expired) {
            return <div style={HISTORY_THUMBNAIL_STATUS_STYLE}>{t('history.mediaExpired')}</div>
        }

        if (!overlayImagePath) {
            return <span style={{ color: '#999' }}>{emptyText}</span>
        }

        if (
            shouldUseBasicBoxOnlyHistoryImage(record) &&
            record.image_path
        ) {
            const shouldLoadThumbnail = thumbnailIndex < enabledThumbnailCount
            if (!shouldLoadThumbnail) {
                return <div style={HISTORY_THUMBNAIL_PLACEHOLDER_STYLE} />
            }

            return (
                <ReadonlyDetectionOverlayImage
                    imageSrc={record.image_path}
                    predictions={record.results}
                    alt={alt}
                    width={80}
                    height={60}
                    className="device-history-thumbnail device-history-box-only-thumbnail"
                    preview
                    previewTitle={alt}
                    containPreview
                    emptyText={t('history.imageLoadFailed')}
                    strokeColor={BASIC_DEVICE_BOX_COLOR}
                    colorizePredictions={false}
                    strokeWidth={1.4}
                    borderRadius={4}
                />
            )
        }

        return (
            <ReadonlyDetectionOverlayImage
                imageSrc={record.image_path}
                fallbackImageSrc={fallbackAnnotatedImagePath}
                predictions={record.results}
                alt={alt}
                width={80}
                height={60}
                className="device-history-thumbnail device-history-annotated-thumbnail"
                preview
                previewTitle={alt}
                containPreview
                emptyText={t('history.imageLoadFailed')}
                colorizePredictions
                strokeWidth={1.4}
                borderRadius={4}
            />
        )
    }

    const columns: ColumnsType<DeviceDetectionRecord> = [
        {
            title: t('history.columns.detectionTime'),
            dataIndex: 'created_at',
            key: 'created_at',
            width: 180,
            render: (text: string) => formatDate(text),
        },
        {
            title: t('history.columns.originalImage'),
            dataIndex: 'image_path',
            key: 'image_path',
            width: 120,
            render: (path: string | null, record, index) =>
                renderHistoryImage(
                    record,
                    path,
                    t('history.columns.originalImage'),
                    t('history.noImage'),
                    index * 2
                ),
        },
        {
            title: t('history.columns.annotatedImage'),
            dataIndex: 'annotated_path',
            key: 'annotated_path',
            width: 120,
            render: (_path: string | null, record, index) =>
                renderAnnotatedHistoryImage(record, index * 2 + 1),
        },
        {
            title: t('history.columns.mosquitoCount'),
            dataIndex: 'mosquito_count',
            key: 'mosquito_count',
            width: 100,
            render: (_count: number, record) => {
                const count = getHistoryTrendCount(
                    record,
                    selectedPestTypes,
                    supportsSpeciesRecognition,
                    pestTypeSelectOptions
                )
                return (
                    <Tag color={count > 0 ? 'red' : 'default'}>
                        {t('history.mosquitoCountUnit', { count })}
                    </Tag>
                )
            },
        },
        {
            title: t('history.columns.aiDetails'),
            dataIndex: 'results',
            key: 'results',
            render: (
                results: Array<Record<string, unknown>> | null,
                record
            ) => {
                if (
                    !record.feature_keys_at_upload?.includes(
                        FEATURE_HISTORY_TRENDS
                    )
                ) {
                    return (
                        <span style={{ color: '#999' }}>
                            {t('history.unsupportedHistory')}
                        </span>
                    )
                }

                if (
                    !record.feature_keys_at_upload?.includes(
                        FEATURE_MOSQUITO_SPECIES_RECOGNITION
                    )
                ) {
                    return (
                        <span style={{ color: '#999' }}>
                            {t('history.unsupportedRecognition')}
                        </span>
                    )
                }

                if (!results || results.length === 0) {
                    return (
                        <span style={{ color: '#999' }}>
                            {t('history.noResults')}
                        </span>
                    )
                }

                const classCount: Partial<Record<MosquitoDisplayCategory, number>> = {}
                results.forEach((item) => {
                    const category = getPredictionDisplayCategory(item)
                    if (category) {
                        classCount[category] = (classCount[category] || 0) + 1
                    }
                })
                const visibleCategories = sortMosquitoDisplayCategories(
                    Object.entries(classCount)
                        .filter(([, count]) => (count ?? 0) > 0)
                        .map(([category]) => category as MosquitoDisplayCategory)
                )

                return (
                    <div>
                        {visibleCategories.map(
                            (category) => (
                                <Tag
                                    key={category}
                                    color="blue"
                                    style={{ marginBottom: 4 }}
                                >
                                    {getMosquitoCategoryDisplayName(
                                        category,
                                        t,
                                        HISTORY_DETECTION_CLASS_DISPLAY_PREFIX
                                    )}: {classCount[category]}
                                </Tag>
                            )
                        )}
                    </div>
                )
            },
        },
    ]

    return (
        <Modal
            title={
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        minWidth: 0,
                    }}
                >
                    <span>{t('history.title')}</span>
                    {deviceTitleLabel && (
                        <Tag
                            color="blue"
                            style={{
                                marginLeft: 12,
                                maxWidth: 260,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {deviceTitleLabel}
                        </Tag>
                    )}
                </div>
            }
            open={visible}
            onCancel={onClose}
            footer={null}
            width={1100}
            closeIcon={<CloseOutlined />}
            destroyOnClose
            maskClosable={false}
            style={HISTORY_MODAL_STYLE}
            styles={{ body: HISTORY_MODAL_BODY_STYLE }}
        >
            <div className="device-history-summary-grid">
                {deviceSummaryItems.map((item) => (
                    <div
                        className="device-history-summary-card"
                        key={item.label}
                    >
                        <span>{item.label}</span>
                        {item.isTag ? (
                            <Tag className="device-history-summary-tag" color="blue">
                                {item.value}
                            </Tag>
                        ) : (
                            <strong title={item.value}>{item.value}</strong>
                        )}
                    </div>
                ))}
            </div>

            <FilterBar className="device-history-modal-filter">
                <FilterField
                    className="device-history-modal-filter-date"
                    label={t('history.filters.dateRange')}
                >
                    <ReadonlyRangePicker
                        value={dateRange}
                        onChange={(dates) => {
                            const nextDateRange =
                                dates && dates[0] && dates[1]
                                    ? ([dates[0], dates[1]] as [Dayjs, Dayjs])
                                    : null
                            setDateRange(nextDateRange)
                            setCurrentPage(1)
                            void fetchData(
                                1,
                                pageSize,
                                nextDateRange,
                                selectedPestTypes
                            )
                        }}
                    />
                </FilterField>

                <FilterField
                    className="device-history-modal-filter-pest-type"
                    label={t('history.filters.pestType')}
                >
                    <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        value={getAllAwareDisplayValues(selectedPestTypes)}
                        options={pestTypeSelectOptions}
                        placeholder={t('history.filters.pestTypePlaceholder')}
                        optionFilterProp="label"
                        filterOption={filterSearchOptionByLabel}
                        maxTagCount="responsive"
                        onChange={(value) => {
                            const nextPestTypes = sanitizePestTypesForSpeciesSupport(
                                normalizeAllAwareValues(
                                    value as FilterSelectValue<string>[],
                                    selectedPestTypes
                                ),
                                supportsSpeciesRecognition
                            )
                            setSelectedPestTypes(nextPestTypes)
                            setCurrentPage(1)
                            void fetchData(
                                1,
                                pageSize,
                                dateRange,
                                nextPestTypes
                            )
                        }}
                    />
                </FilterField>

                <FilterActions className="device-history-modal-filter-actions">
                    <Button onClick={handleResetFilters}>
                        {t('history.filters.reset')}
                    </Button>
                    <Button
                        disabled={historyLoading}
                        icon={<ReloadOutlined />}
                        onClick={handleRefresh}
                    >
                        {t('history.refreshData')}
                    </Button>
                </FilterActions>
            </FilterBar>

            <div className="device-history-modal-view-switch">
                <Button
                    icon={<UnorderedListOutlined />}
                    type={activeView === 'list' ? 'primary' : 'default'}
                    onClick={() => setActiveView('list')}
                >
                    {t('history.views.list', { defaultValue: '列表' })}
                </Button>
                <Button
                    icon={<LineChartOutlined />}
                    type={activeView === 'trend' ? 'primary' : 'default'}
                    onClick={() => setActiveView('trend')}
                >
                    {t('history.views.trend', { defaultValue: '走势图' })}
                </Button>
            </div>

            <Spin
                spinning={activeView === 'trend' ? trendLoading : historyLoading}
                wrapperClassName="device-history-modal-content"
            >
                {activeView === 'trend' ? (
                    trendErrorMessage ? (
                        <Alert message={trendErrorMessage} showIcon type="error" />
                    ) : trendSeries.length > 0 ? (
                        <div className="device-history-trend-panel">
                            <div className="device-history-trend-summary">
                                <div>
                                    <span>
                                        {t('history.trend.total', {
                                            defaultValue: '总蚊虫数',
                                        })}
                                    </span>
                                    <strong>
                                        {t('history.mosquitoCountUnit', {
                                            count: trendTotalCount,
                                        })}
                                    </strong>
                                </div>
                                <div>
                                    <span>
                                        {t('history.trend.peak', {
                                            defaultValue: '峰值',
                                        })}
                                    </span>
                                    <strong>
                                        {t('history.mosquitoCountUnit', {
                                            count: trendPeak?.count ?? 0,
                                        })}
                                    </strong>
                                    <small>{trendPeak?.date ?? '-'}</small>
                                </div>
                                <div>
                                    <span>
                                        {t('history.trend.average', {
                                            defaultValue: '日均',
                                        })}
                                    </span>
                                    <strong>
                                        {t('history.mosquitoCountUnit', {
                                            count: Number(
                                                trendAverageCount.toFixed(1)
                                            ),
                                        })}
                                    </strong>
                                </div>
                            </div>

                            <div className="device-history-trend-chart">
                                <svg
                                    viewBox={`0 0 ${HISTORY_TREND_CHART_WIDTH} ${HISTORY_TREND_CHART_HEIGHT}`}
                                    role="img"
                                    aria-label={t('history.views.trend', {
                                        defaultValue: '走势图',
                                    })}
                                    preserveAspectRatio="none"
                                    onMouseMove={handleTrendMouseMove}
                                    onMouseLeave={handleTrendMouseLeave}
                                >
                                    {trendYAxisTicks.map((tick) => (
                                        <g key={`y-${tick.value}-${tick.y}`}>
                                            <text
                                                x={HISTORY_TREND_CHART_PADDING_LEFT - 10}
                                                y={tick.y + 4}
                                                textAnchor="end"
                                                className="device-history-trend-axis-label"
                                            >
                                                {tick.value}
                                            </text>
                                            <line
                                                x1={HISTORY_TREND_CHART_PADDING_LEFT}
                                                x2={
                                                    HISTORY_TREND_CHART_WIDTH -
                                                    HISTORY_TREND_CHART_PADDING_RIGHT
                                                }
                                                y1={tick.y}
                                        y2={tick.y}
                                        className="device-history-trend-grid-line"
                                    />
                                </g>
                            ))}
                                    <line
                                        x1={HISTORY_TREND_CHART_PADDING_LEFT}
                                        x2={HISTORY_TREND_CHART_PADDING_LEFT}
                                        y1={HISTORY_TREND_CHART_PADDING_TOP}
                                        y2={
                                            HISTORY_TREND_CHART_HEIGHT -
                                            HISTORY_TREND_CHART_PADDING_BOTTOM
                                        }
                                        className="device-history-trend-axis-line"
                                    />
                                    {trendXAxisTicks.map((point) => (
                                        <g key={`x-${point.date}-${point.x}`}>
                                            <line
                                                x1={point.x}
                                                x2={point.x}
                                                y1={HISTORY_TREND_CHART_PADDING_TOP}
                                                y2={
                                                    HISTORY_TREND_CHART_HEIGHT -
                                                    HISTORY_TREND_CHART_PADDING_BOTTOM
                                                }
                                                className="device-history-trend-grid-line"
                                            />
                                            <text
                                                x={point.x}
                                                y={HISTORY_TREND_CHART_HEIGHT - 12}
                                                textAnchor="middle"
                                                className="device-history-trend-axis-label"
                                            >
                                                {formatTrendAxisDate(point.date)}
                                            </text>
                                        </g>
                                    ))}
                                    <line
                                        x1={HISTORY_TREND_CHART_PADDING_LEFT}
                                        x2={
                                            HISTORY_TREND_CHART_WIDTH -
                                            HISTORY_TREND_CHART_PADDING_RIGHT
                                        }
                                        y1={
                                            HISTORY_TREND_CHART_HEIGHT -
                                            HISTORY_TREND_CHART_PADDING_BOTTOM
                                        }
                                        y2={
                                            HISTORY_TREND_CHART_HEIGHT -
                                            HISTORY_TREND_CHART_PADDING_BOTTOM
                                        }
                                        className="device-history-trend-axis-line"
                                    />
                                    {hoveredTrendPoint && (
                                        <line
                                            x1={hoveredTrendPoint.x}
                                            x2={hoveredTrendPoint.x}
                                            y1={HISTORY_TREND_CHART_PADDING_TOP}
                                            y2={
                                                HISTORY_TREND_CHART_HEIGHT -
                                                HISTORY_TREND_CHART_PADDING_BOTTOM
                                            }
                                            className="device-history-trend-hover-line"
                                        />
                                    )}
                                    <path
                                        d={trendLinePath}
                                        className="device-history-trend-line"
                                    />
                                    {trendPoints.map((point) => (
                                        <circle
                                            key={`${point.date}-${point.x}`}
                                            cx={point.x}
                                            cy={point.y}
                                            r="3.5"
                                            className="device-history-trend-point"
                                        />
                                    ))}
                                    {hoveredTrendPoint && (
                                        <circle
                                            cx={hoveredTrendPoint.x}
                                            cy={hoveredTrendPoint.y}
                                            r="5"
                                            className="device-history-trend-point is-active"
                                        />
                                    )}
                                    <rect
                                        x="0"
                                        y="0"
                                        width={HISTORY_TREND_CHART_WIDTH}
                                        height={HISTORY_TREND_CHART_HEIGHT}
                                        className="device-history-trend-hover-layer"
                                    />
                                </svg>
                                {hoveredTrendPoint && (
                                    <div
                                        className="device-history-trend-tooltip"
                                        data-placement={trendTooltipPlacement}
                                        style={{
                                            left: `${(hoveredTrendPoint.x / HISTORY_TREND_CHART_WIDTH) * 100}%`,
                                            top: `${(hoveredTrendPoint.y / HISTORY_TREND_CHART_HEIGHT) * 100}%`,
                                        }}
                                    >
                                        <div>{hoveredTrendPoint.date}</div>
                                        <strong>
                                            {t('history.trend.tooltipCount', {
                                                count: hoveredTrendPoint.count,
                                                defaultValue: `${hoveredTrendPoint.count} 只`,
                                            })}
                                        </strong>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        !trendLoading && (
                            <Empty
                                description={t('history.noTrendData', {
                                    defaultValue: '暂无趋势数据',
                                })}
                                style={{ padding: '40px 0' }}
                            />
                        )
                    )
                ) : errorMessage ? (
                    <Alert message={errorMessage} showIcon type="error" />
                ) : data && data.items.length > 0 ? (
                    <div className="device-history-modal-list">
                        <div
                            className="device-history-modal-table-frame"
                        >
                            <Table
                                className="device-history-modal-table"
                                columns={columns}
                                dataSource={data.items}
                                rowKey="id"
                                pagination={false}
                                scroll={{ x: 900 }}
                            />
                        </div>
                        {historyPagination ? (
                            <Pagination
                                {...historyPagination}
                                className="device-history-modal-pagination"
                            />
                        ) : null}
                    </div>
                ) : (
                    !historyLoading && (
                        <Empty
                            description={t('history.noHistory')}
                            style={{ padding: '40px 0' }}
                        />
                    )
                )}
            </Spin>
        </Modal>
    )
}

export default DeviceHistoryModal
